// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.GoogleMapsActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import 'share/utils/Izenda.StringUtilities';
import { MappingKey, ContainerName } from 'logic/data/Izenda.ReportPartMapConstants';
import { toJS } from 'mobx';
import { compose, withProps } from 'recompose';
import MapStaticContentStore from 'logic/Izenda.MapStaticContentStore';
import { getColorSettingsRange, getValueFromMappingField } from 'share/utils/Izenda.ProcessDataUtils';
import { getFieldValue } from 'share/utils/Izenda.ReportPartUtils';
import {
  getOr,
  first,
  get,
  isNil,
  head,
  has,
  isFunction,
  each
} from 'lodash/fp';
import { isValidLatLong } from './googlemaps/Izenda.GoogleMapsUtils';

/**
 * Build series to send to hightmap
 * @param {any} mapOptions
 * @param {any} mapData
 * @param {any} backgroundMap
 * @param {any} activePointOption
 * @param {any} mappingData
 * @param {Function} fnUpdateMappingData
 */
function getSeriesMap(
  mapOptions: any,
  mapData: any,
  activePointOption: any,
  mappingData: any,
  fnUpdateMappingData: Function,
  getGeoPoint: Function
) {
  const dataStructure = getOr({}, 'dataStructure', mapData);
  const fieldsMapping = getOr([], 'fieldsMapping', mapData);

  const shadingSeries = {}; // shading map
  const bubbleSeries = {}; // bubble map

  const activePointOptionType = get('pointType', activePointOption);
  const isUserDefinedPoint = activePointOptionType === ContainerName.LATITUDE;
  const shownRecords = [];

  let mappingKeys,
    firstMappingKey,
    shadingField,
    bubbleFields,
    activePointVal,
    activePointField,
    activePointGEOInfo,
    pointCode,
    records,
    commonBubbleOptions;

  if (activePointOption && (activePointField = first(dataStructure[activePointOptionType]))) {
    mappingKeys = MappingKey[activePointOptionType];
    firstMappingKey = first(mappingKeys);

    records = toJS(getOr([], 'rawData.records', mapData));

    if (
      [
        ContainerName.COUNTRY,
        ContainerName.STATE,
        ContainerName.CITY,
        ContainerName.COUNTY,
        ContainerName.POSTAL_CODE,
        ContainerName.ADDRESS
      ].indexOf(activePointOptionType) > -1 &&
      isNil(mappingData)
    ) {
      loadMappingData(
        activePointOptionType,
        mapData,
        getValueFromMappingField(activePointField, records),
        fnUpdateMappingData
      );
      return {};
    } else if (isUserDefinedPoint) {
      commonBubbleOptions = {
        latField: head(dataStructure[ContainerName.LATITUDE]),
        lonField: head(dataStructure[ContainerName.LONGTITUDE]),
        isUserDefinedPoint: isUserDefinedPoint
      };
    }

    shadingField = first(dataStructure[ContainerName.SHADING_VALUES]);
    bubbleFields = dataStructure[ContainerName.BUBBLE_VALUES];

    // init data
    shadingField && initMapSerieData(shadingSeries, shadingField, activePointOption);
    bubbleFields &&
      bubbleFields.forEach(function(bubbleField) {
        initMapSerieData(bubbleSeries, bubbleField, activePointOption, commonBubbleOptions);
      });

    // loop preview-records & build series
    records.forEach(record => {
      activePointVal = getValueFromMappingField(activePointField, record);

      if (!activePointVal) {
        return true; // break forEach
      }

      if (isUserDefinedPoint) {
        // bubble series - get data from lat/long fields
        if (bubbleFields && addBubbleData(activePointOption, bubbleSeries, null, null, null, record, fieldsMapping)) {
          shownRecords.push(record);
        }
        return true; // break forEach
      }

      activePointGEOInfo = getGeoPoint(get(activePointVal, mappingData), record, dataStructure, activePointOptionType);
      //activePointGEOInfo = flow([get(activePointVal), head])(mappingData);

      // mis-matched point
      if (!activePointGEOInfo) {
        return true; // break forEach
      }

      pointCode = get(firstMappingKey.key, activePointGEOInfo.properties);

      let trackShadingRecord = false;
      let trackBubbleRecord = false;
      // shading serie (only one serie)
      if (shadingField) {
        addPoint(
          activePointOption,
          shadingSeries,
          shadingField,
          record,
          pointCode,
          activePointVal,
          activePointGEOInfo,
          fieldsMapping
        );
        trackShadingRecord = true;
      }

      // bubble series
      if (bubbleFields) {
        trackBubbleRecord = addBubbleData(
          activePointOption,
          bubbleSeries,
          activePointVal,
          pointCode,
          activePointGEOInfo,
          record
        );
      }

      (trackShadingRecord || trackBubbleRecord) && shownRecords.push(record);
    });
  }

  return {
    pointOption: activePointOption,
    shadingSeries: shadingSeries,
    bubbleSeries: bubbleSeries,
    pointMap: [],
    shownRecords
  };

  /**
   * init map serie data
   * @param {any} source
   * @param {any} field
   * @param {any} activePointOption
   * @param {any} commonOptions
   */
  function initMapSerieData(source: any, field: any, activePointOption: any, commonOptions: any) {
    source[field.fieldNameAlias] = $izenda.extend(true, {}, commonOptions, {
      sum: 0,
      min: Number.MAX_VALUE,
      max: Number.MIN_VALUE,
      data: [],
      field: field,
      activePointOption: activePointOption
    });
  }

  /**
   * get data for bubbleSeries field
   * @param {any} activePointOption
   * @param {any} bubbleSeries
   * @param {string} activePointVal
   * @param {string} pointCode
   * @param {any} activePointGEOInfo
   * @param {any} record
   */
  function addBubbleData(
    activePointOption: any,
    bubbleSeries: any,
    activePointVal: string,
    pointCode: string,
    activePointGEOInfo: any,
    record: any,
    fieldsMapping: any[]
  ) {
    let trackRecord = false;
    bubbleFields.forEach(bubbleField => {
      addPoint(
        activePointOption,
        bubbleSeries,
        bubbleField,
        record,
        pointCode,
        activePointVal,
        activePointGEOInfo,
        fieldsMapping
      );
      trackRecord = true;
    });
    return trackRecord;
  }

  /**
   * Get "value" type color settings (high + low)
   * @param {any} colorSettings color settings object:
   * {
   *  rangePercent: {...},
   *  rangeValue: {},
   *  value: [{
   *    key: 0, // static value
   *    thresholdType: '', // 'static'|'dynamic'
   *    fields: [], // list of selected field models (SettingsByRangeItemFieldModel)
   *  }]
   * }
   * @param {any[]} fieldsMapping mapping for fieldsId -> columnName
   * @param {any} record current data record
   * @param {number} pointVal current point
   * @return {any} min/max values.
   */
  function colorSettingsDynamicRanges(colorSettings, fieldsMapping, record, pointVal): any {
    return getColorSettingsRange(colorSettings, fieldsMapping, record, pointVal);
  }

  /**
   * Add point
   * @param {any} activePointOption
   * @param {any} source
   * @param {any} field
   * @param {any} record
   * @param {any} pointCode
   * @param {any} activePointVal
   * @param {any} activePointGEOInfo
   */
  function addPoint(
    activePointOption: any,
    source: any,
    field: any,
    record: any,
    pointCode: any,
    activePointVal: any,
    activePointGEOInfo: any,
    fieldsMapping: any[]
  ) {
    const pointType = getOr('', 'pointType', activePointOption);

    activePointGEOInfo = get('properties', activePointGEOInfo) || activePointGEOInfo || {};

    const serie = source[field.fieldNameAlias];
    const fieldValue = field.columnName ? getFieldValue(field.columnName, record) : {};
    const pointVal = fieldValue.originalValue || fieldValue.value;

    // get color settings for current map zoom level (contry/state/city/...)
    const currentPointColorSettings = getOr(
      null,
      `reportPartElm.properties.dataFormattings.color.cellColor.${pointType}`,
      field
    );
    // find color ranges for current zoom level
    const rangesObject = colorSettingsDynamicRanges(currentPointColorSettings, fieldsMapping, record, pointVal);

    const pointInfo = {
      originalValue: fieldValue.originalValue,
      value: pointVal,
      rangesObject: rangesObject,
      formatedValue: fieldValue.originalValue,
      pointName: activePointVal,
      pointInfo: activePointGEOInfo,
      valueField: field,
      drillingDownField: first(dataStructure[activePointOption.pointType]),
      nameField: activePointOption,
      record: record
    };

    pointCode && (pointInfo.code = pointCode);

    if (has('latField', serie)) {
      activePointGEOInfo.latitude = getValueFromMappingField(serie.latField, record);
    }

    if (has('lonField', serie)) {
      activePointGEOInfo.longitude = getValueFromMappingField(serie.lonField, record);
    }

    if (activePointGEOInfo.longitude && activePointGEOInfo.latitude) {
      $izenda.extend(pointInfo, {
        lon: Number(activePointGEOInfo.longitude),
        lat: Number(activePointGEOInfo.latitude)
      });

      if (isValidLatLong(activePointGEOInfo.latitude, activePointGEOInfo.longitude)) {
        serie.sum += pointVal;
        serie.min = Math.min(serie.min, pointVal);
        serie.max = Math.max(serie.max, pointVal);
      }
    }

    serie.data.push(pointInfo);
  }
}

/**
 * Load mapping data.
 * @param {string} pointType
 * @param {any} mapData
 * @param {any} values
 * @param {Function} callback
 */
function loadMappingData(pointType: string, mapData: any, values: any, callback: Function) {
  let fnName = '';
  let mappingValues = [];

  switch (pointType) {
    case ContainerName.COUNTRY:
      fnName = 'getCountryCodes';
      mappingValues = toJS(getOr([], 'countryCodes', mapData));
      break;
    case ContainerName.STATE:
      fnName = 'getStateCodes';
      mappingValues = toJS(getOr([], 'stateCodes', mapData));
      break;
    case ContainerName.COUNTY:
      fnName = 'getCountiesGGMap';
      mappingValues = toJS(getOr([], 'countyCodes', mapData));
      break;
    case ContainerName.CITY:
      fnName = 'getCities';
      mappingValues = toJS(getOr([], 'cities', mapData));
      break;
    case ContainerName.POSTAL_CODE:
      fnName = 'getPostalCode';
      mappingValues = toJS(getOr([], 'postalCodes', mapData));
      break;
    case ContainerName.ADDRESS:
      fnName = 'getAddressesGGMap';
      mappingValues = toJS(getOr([], 'addressCodes', mapData));
      break;
    default:
    // do nothing
  }

  fnName = MapStaticContentStore[fnName];
  return isFunction(fnName) && fnName(values, mappingValues).then(callback);
}

/**
 * Filter records based lat/lon
 * @param {Object} mapCom
 * @param {Object} bubbleSeries
 */
function filterRecordsByLatLon(mapCom: any, bubbleSeries: any) {
  const records = [];

  each(serie => {
    each(point => {
      isValidLatLong(point.lat, point.lon) && records.push(point.record);
    }, serie.data);
  }, bubbleSeries);

  return records;
}

module.exports = compose(
  withProps(props => {
    const activePointOption = get('commonOptions.izendaOptions.activePointOption', props.chartOptions);

    function onUpdateMappingData(mappingData) {
      props.setMapData('mappingData', mappingData);
      return null;
    }

    return {
      seriesMap: getSeriesMap(
        props.chartOptions,
        props.chartData,
        activePointOption,
        props.mappingData,
        onUpdateMappingData,
        props.getGeoPoint
      ),
      filterRecordsByLatLon
    };
  })
);



// WEBPACK FOOTER //
// ./ui/share/components/charting/Izenda.GoogleMapsActions.js